// src/lib/conference/conference-session.ts
// Orchestriert die Konferenz je Rolle: eigener Raum-WS (Warteraum, Roster, Steuerbefehle) + LiveKit
// für die Medien (seit 2026-09-25, vorher Cloudflare Realtime über calls.ts). Die äußere Schnittstelle
// der drei Klassen ist unverändert (useHostConference, GuestRoom, Speaker programmieren dagegen).
//
// Was mit LiveKit ersatzlos entfallen ist: sessionId/trackName-Referenzen im Raum, die opChain-
// Serialisierung der Renegotiations (SFU-406/410), expectingHostVideo/pendingCamKey und der Roster-
// Reconcile. LiveKit liefert zu jedem Track den Teilnehmer — die Zuordnung ist eindeutig (Identität,
// shared/conference-identity.ts), auch bei Host-Rejoin (die „erster Audio-Track = Host-Mix"-Annahme
// der A/V-Sync ist damit weg).
import { Track, AudioPresets, type RemoteParticipant, type RemoteTrack, type RemoteTrackPublication } from 'livekit-client';
import { ConferenceSignaling } from './conference-signaling';
import { MediaLink, fetchLivekitToken } from './media-link';
import { GuestReturnMix } from './audio-mix';
import { warmUpAudioSession } from './ios-warmup';
import { reZeroGuestVideo, setVideoDelay } from './av-sync-apply';
import { DEFAULT_OFFSET_MS } from './av-sync-offset';
import { isHostIdentity, tileKeyOf } from '../../../shared/conference-identity';
import type { ConferenceMessage, ConfGuest, ConfPeer, ConfSpeakerRef } from '../../../shared/conference-protocol';

export type VideoResolution = '360p' | '720p' | '1080p';
const RES_DIMS: Record<VideoResolution, { width: number; height: number }> = {
  '360p': { width: 640, height: 360 },
  '720p': { width: 1280, height: 720 },
  '1080p': { width: 1920, height: 1080 },
};

function wsUrl(path: string): string {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}${path}`;
}

/** Kachel-Schlüssel eines Teilnehmers (`g:<id>` / `sp:<id>`), Fallback für Unbekanntes. */
function tileKey(p: RemoteParticipant, track: RemoteTrack): string {
  return tileKeyOf(p.identity) ?? `unknown:${track.sid ?? track.mediaStreamTrack.id}`;
}

/** Remote-Audio per eigenem <audio> + setSinkId ausgeben (SP2-Pfad, KEIN WebAudio — 760-ms-Falle). */
function playRemoteAudio(track: MediaStreamTrack, outputDeviceId?: string): HTMLAudioElement {
  const el = new Audio();
  el.autoplay = true;
  el.srcObject = new MediaStream([track]);
  const out = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
  if (out.setSinkId && outputDeviceId) { void out.setSinkId(outputDeviceId).catch(() => {}); }
  void el.play().catch(() => {});
  return el;
}

// ── Host ──────────────────────────────────────────────────────────────────────
export type HostConferenceHandlers = {
  onRoster: (guests: ConfGuest[]) => void;
  onStateChange?: (s: 'connecting' | 'live' | 'reconnecting' | 'ended') => void;
  onLocalVideo?: (stream: MediaStream | null) => void;  // Host-Self-Preview der Capture (null = weg)
  onPeerVideo?: (key: string, track: MediaStreamTrack | null) => void;  // Cockpit-Cam-Kachel (null = weg)
};

export class HostConference {
  private sig: ConferenceSignaling | null = null;
  private link: MediaLink | null = null;
  private mix = new GuestReturnMix();
  private videoTrack: MediaStreamTrack | null = null;
  private hostmixTrack: MediaStreamTrack | null = null;
  private liveCamKeys = new Set<string>();

  // Zweistufig: Video-Basis und Audio-Overlay sind unabhängig an/abschaltbar.
  private audioEnabled = false;
  private videoEnabled = false;
  private audioStarting = false;   // In-Flight-Guard (Doppelklick) — trennt „läuft an" von „läuft"
  private videoStarting = false;

  constructor(private roomName: string, private handlers: HostConferenceHandlers) {}

  private get mixOpts() {
    // Programm-Mix aus Nuendo: DTX aus (Stille-Unterdrückung erzeugt Artefakte, vgl. L16-Befund
    // 2026-05-29), RED an, Musik-Preset statt Sprach-Bitrate.
    return { source: Track.Source.Microphone, name: `hostmix-${this.roomName}`, dtx: false, red: true, audioPreset: AudioPresets.musicHighQuality };
  }
  private get videoOpts() {
    return { source: Track.Source.Camera, name: `hostvideo-${this.roomName}`, simulcast: true };
  }

  /** Legt Raum-WS + LiveKit-Verbindung einmal an (egal ob Video, Audio oder Empfang zuerst kommt). */
  private ensureSession(): MediaLink {
    if (this.link) return this.link;
    this.handlers.onStateChange?.('connecting');
    const link = new MediaLink(
      () => fetchLivekitToken(`/api/admin/conference/${encodeURIComponent(this.roomName)}/token`, {}),
      false,   // Abos steuert der Host selbst: Cams immer, Gäste-Mikros nur bei aktivem Konferenz-Audio
      {
        onConnected: async () => {
          if (this.hostmixTrack) await link.publish(this.hostmixTrack, this.mixOpts);
          if (this.videoTrack) await link.publish(this.videoTrack, this.videoOpts);
        },
        onPublished: (pub, p) => this.decide(pub, p),
        onSubscribed: (track, _pub, p) => this.attach(track, p),
        onUnsubscribed: (track, _pub, p) => this.detach(track, p),
        onState: (s) => this.handlers.onStateChange?.(s),
      },
    );
    this.link = link;
    this.sig = new ConferenceSignaling(
      wsUrl(`/api/admin/conference/${encodeURIComponent(this.roomName)}`),
      { type: 'HOST_HELLO' },
      {
        onOpen: () => this.handlers.onStateChange?.('live'),
        onReconnecting: () => this.handlers.onStateChange?.('reconnecting'),
        onMessage: (m) => this.onMessage(m),
      },
    );
    this.sig.open();
    link.start();
    return link;
  }

  /** Abo-Entscheidung je Publikation: Cams (Gäste + Sprecher) immer fürs Monitoring; Gäste-Mikros nur,
   *  wenn das Konferenz-Audio läuft (sonst hört Nuendo sie nicht und sie kosten nur Bandbreite). */
  private decide(pub: RemoteTrackPublication, p: RemoteParticipant): void {
    if (isHostIdentity(p.identity)) return;
    if (pub.kind === Track.Kind.Video) pub.setSubscribed(true);
    else if (pub.kind === Track.Kind.Audio) pub.setSubscribed(this.audioEnabled);
  }

  private attach(track: RemoteTrack, p: RemoteParticipant): void {
    if (track.kind === Track.Kind.Audio) {
      this.mix.addTrack(`${p.identity}:${track.sid}`, track.mediaStreamTrack);   // Gast-Audio → Return-Device → Nuendo
      return;
    }
    // Video = Cockpit-Monitoring-Cam — NIEMALS in den Nuendo-Audio-Mix!
    const key = tileKey(p, track);
    this.liveCamKeys.add(key);
    this.handlers.onPeerVideo?.(key, track.mediaStreamTrack);
  }

  private detach(track: RemoteTrack, p: RemoteParticipant): void {
    if (track.kind === Track.Kind.Audio) { this.mix.removeTrack(`${p.identity}:${track.sid}`); return; }
    const key = tileKey(p, track);
    this.liveCamKeys.delete(key);
    this.handlers.onPeerVideo?.(key, null);
  }

  /** Alle bekannten Gäste-Mikros (ab)bestellen — beim Ein-/Ausschalten des Konferenz-Audios. */
  private resubscribeAudio(): void {
    const room = this.link?.current;
    if (!room) return;
    for (const p of room.remoteParticipants.values()) for (const pub of p.trackPublications.values()) {
      if (pub.kind === Track.Kind.Audio && !isHostIdentity(p.identity)) pub.setSubscribed(this.audioEnabled);
    }
  }

  /** Video-Basis: Studio-Capture publishen (idempotent).
   *  `videoStarting` ist der Doppel-Klick-Schutz WÄHREND des Starts; `videoEnabled` wird bewusst erst
   *  NACH erfolgreichem Publish gesetzt (phase-next-notes R.3). */
  async startVideo(deviceId: string, resolution: VideoResolution): Promise<void> {
    if (this.videoEnabled || this.videoStarting) return;
    this.videoStarting = true;
    try {
      const link = this.ensureSession();
      // Capture-Cards (Elgato) liefern OHNE explizite Auflösung schwarzen 640x480-Default → exact erzwingt
      // einen echten Modus; Fallback auf Default, falls das Device den exakten Modus nicht kann. Deshalb
      // eigenes getUserMedia statt createLocalVideoTrack({resolution}) (kein exact, 12b R6).
      const dim = RES_DIMS[resolution];
      const base = { deviceId: { exact: deviceId } };
      let cam: MediaStream;
      try { cam = await navigator.mediaDevices.getUserMedia({ video: { ...base, width: { exact: dim.width }, height: { exact: dim.height } } }); }
      catch { cam = await navigator.mediaDevices.getUserMedia({ video: base }); }
      this.videoTrack = cam.getVideoTracks()[0];
      this.handlers.onLocalVideo?.(cam);   // Self-Preview: Host sieht sofort, dass die Capture live ist
      await link.waitConnected();
      await link.publish(this.videoTrack, this.videoOpts);
      this.videoEnabled = true;
    } catch (e) {
      try { this.videoTrack?.stop(); } catch { /* ignore */ }
      this.videoTrack = null;
      this.handlers.onLocalVideo?.(null);
      throw e;
    } finally {
      this.videoStarting = false;
    }
  }

  /** Konferenz-Audio-Overlay: hostmix publishen + Gäste-Mikros aufs Return-Output.
   *  Flag-Semantik wie bei startVideo. */
  async enableAudio(inputDeviceId: string, outputDeviceId: string): Promise<void> {
    if (this.audioEnabled || this.audioStarting) return;
    this.audioStarting = true;
    try {
      const link = this.ensureSession();
      await this.mix.setOutputDevice(outputDeviceId);
      // AEC/NS/AGC AUS: der Mix kommt clean aus Nuendo, Browser-DSP würde ihn zerstören.
      const mic = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: inputDeviceId ? { exact: inputDeviceId } : undefined, echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 },
      });
      this.hostmixTrack = mic.getAudioTracks()[0];
      await link.waitConnected();
      await link.publish(this.hostmixTrack, this.mixOpts);
      this.audioEnabled = true;
      this.resubscribeAudio();   // bereits zugelassene Gäste jetzt nach Nuendo holen
    } catch (e) {
      try { this.hostmixTrack?.stop(); } catch { /* ignore */ }
      this.hostmixTrack = null;
      throw e;
    } finally {
      this.audioStarting = false;
    }
  }

  /** Empfangs-Modus: Raum-WS + LiveKit OHNE eigenen Publish. Damit ist der Host im Konferenzraum
   *  „live" (Sprecher joinen dadurch, hostLive) und sieht Sprecher-/Gäste-Cams. Idempotent. */
  async startReceive(): Promise<void> {
    await this.ensureSession().waitConnected();
  }

  /** Take-Grenze (Nuendo-Record-Stop) → Gäste lautlos neu einnordnen (Raum broadcastet RECALIBRATE). */
  recalibrateGuests(): void { this.sig?.send({ type: 'RECALIBRATE' }); }

  private onMessage(m: ConferenceMessage): void {
    if (m.type === 'ROSTER') this.handlers.onRoster(m.guests);
  }

  admit(guestId: string): void { this.sig?.send({ type: 'ADMIT', guestId }); }
  /** Der Raum schließt den Gast-WS UND nimmt ihn serverseitig aus dem LiveKit-Raum (removeParticipant). */
  kick(guestId: string): void { this.sig?.send({ type: 'KICK', guestId }); }

  /** Video-Basis stoppen; Session bleibt, falls Audio noch läuft. */
  stopVideo(): void {
    if (!this.videoEnabled) return;
    this.videoEnabled = false;
    const t = this.videoTrack;
    this.videoTrack = null;
    if (t) void this.link?.unpublish(t).finally(() => { try { t.stop(); } catch { /* ignore */ } });
    this.handlers.onLocalVideo?.(null);
    if (!this.audioEnabled) this.teardown();
  }

  /** Konferenz-Audio-Overlay abbauen; Session bleibt, falls Video noch läuft. */
  disableAudio(): void {
    if (!this.audioEnabled) return;
    this.audioEnabled = false;
    const t = this.hostmixTrack;
    this.hostmixTrack = null;
    if (t) void this.link?.unpublish(t).finally(() => { try { t.stop(); } catch { /* ignore */ } });
    this.mix.close();
    this.resubscribeAudio();   // Gäste-Mikros abbestellen; ein späteres enableAudio bestellt sie neu
    if (!this.videoEnabled) this.teardown();
  }

  end(): void {
    this.sig?.send({ type: 'END' });
    this.handlers.onStateChange?.('ended');
    this.teardown();
  }

  teardown(): void {
    try { this.sig?.close(); } catch { /* ignore */ }
    this.link?.stop();
    this.mix.close();
    try { this.videoTrack?.stop(); } catch { /* ignore */ }
    try { this.hostmixTrack?.stop(); } catch { /* ignore */ }
    this.liveCamKeys.clear();
    this.sig = null; this.link = null;
    this.videoTrack = null; this.hostmixTrack = null;
    this.audioEnabled = false; this.videoEnabled = false;
  }
}

// ── Gast ──────────────────────────────────────────────────────────────────────
export type GuestConferenceHandlers = {
  onState: (s: 'waiting' | 'live' | 'reconnecting' | 'kicked' | 'ended' | 'room-full') => void;
  onPeers?: (guests: ConfPeer[], speakers: ConfSpeakerRef[]) => void;
  onHostVideo?: (track: MediaStreamTrack) => void;                       // Studio-Feed
  onPeerVideo?: (key: string, track: MediaStreamTrack | null) => void;  // Cam-Kachel (null = weg)
  onLocalVideo?: (track: MediaStreamTrack | null) => void;              // eigenes Kamerabild (Self-View)
};

export class GuestConference {
  private sig: ConferenceSignaling | null = null;
  private link: MediaLink | null = null;
  private mic: MediaStreamTrack | null = null;
  private camTrack: MediaStreamTrack | null = null;
  private audioEls = new Map<string, HTMLAudioElement>();
  private hostVideoReceiver: RTCRtpReceiver | null = null;
  private hostAudioReceiver: RTCRtpReceiver | null = null;
  private videoTargetMs = 0;
  private liveCamKeys = new Set<string>();
  private peers: { guests: ConfPeer[]; speakers: ConfSpeakerRef[] } = { guests: [], speakers: [] };

  constructor(
    private token: string, private guestId: string, private name: string,
    private opts: { password?: string; inputDeviceId?: string; outputDeviceId?: string; cameraDeviceId?: string },
    private handlers: GuestConferenceHandlers,
  ) {}

  async join(): Promise<void> {
    await warmUpAudioSession();   // iOS-Chipmunk-Schutz, VOR getUserMedia (SP0)
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: this.opts.inputDeviceId ? { exact: this.opts.inputDeviceId } : undefined, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.mic = stream.getAudioTracks()[0];
    const pw = this.opts.password ? `?pw=${encodeURIComponent(this.opts.password)}` : '';
    this.sig = new ConferenceSignaling(
      wsUrl(`/api/conference/${encodeURIComponent(this.token)}${pw}`),
      { type: 'HELLO', guestId: this.guestId, name: this.name },
      {
        onReconnecting: () => this.handlers.onState('reconnecting'),
        // Fallback bei verlorener KICKED/SESSION_ENDED-App-Message: der Server-Close beendet den Gast trotzdem.
        onTerminated: (code) => { this.handlers.onState(code === 4003 ? 'ended' : 'kicked'); this.teardown(); },
        onMessage: (m) => this.onMessage(m),
      },
    );
    this.sig.open();
  }

  private onMessage(m: ConferenceMessage): void {
    switch (m.type) {
      case 'WAITING': this.dropMedia(); this.handlers.onState('waiting'); return;
      case 'ROOM_FULL': this.handlers.onState('room-full'); return;
      case 'KICKED': this.handlers.onState('kicked'); this.teardown(); return;
      case 'SESSION_ENDED': this.handlers.onState('ended'); this.teardown(); return;
      case 'ADMITTED': this.onAdmitted(); return;
      case 'PEERS': this.peers = { guests: m.guests, speakers: m.speakers }; this.emitPeers(); return;
      case 'RECALIBRATE': void this.reZero(); return;
      default: return;
    }
  }

  /** Zugelassen (auch nach WS-Reconnect): LiveKit-Token holen (Gate + Zulassung) und verbinden. */
  private onAdmitted(): void {
    if (this.link) { if (this.link.current) this.handlers.onState('live'); return; }
    const link = new MediaLink(
      () => fetchLivekitToken(`/api/conference/${encodeURIComponent(this.token)}/token`, { guestId: this.guestId, pw: this.opts.password }),
      true,   // Gast hört Host-Mix + Mitgäste und sieht alle Cams — Sprecher haben keinen Audio-Track
      {
        onConnected: async () => {
          if (this.mic) await link.publish(this.mic, { source: Track.Source.Microphone, name: `mic-${this.guestId}` });
          if (this.camTrack) await link.publish(this.camTrack, { source: Track.Source.Camera, name: `cam-${this.guestId}` });
          else if (this.opts.cameraDeviceId) void this.publishCam();   // eigene Erfassung, blockiert das Mikro nicht
        },
        onSubscribed: (track, _pub, p) => this.attachRemote(track, p),
        onUnsubscribed: (track, _pub, p) => this.detachRemote(track, p),
        onState: (s) => this.handlers.onState(s),
        onTerminal: () => {
          // Aus dem Medienraum genommen (Kick/Ende/Grace-Ablauf) oder in einem anderen Tab geöffnet:
          // Medien weg; den Zustand bestimmt der Raum-WS (KICKED/SESSION_ENDED/WAITING/ADMITTED).
          this.link = null;
          this.clearRemote();
        },
      },
    );
    this.link = link;
    link.start();
  }

  /** Kamera aufnehmen (optional) und publishen, sobald die Verbindung steht. */
  private async publishCam(): Promise<void> {
    if (this.camTrack) return;
    let cam: MediaStream;
    try { cam = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: this.opts.cameraDeviceId! } } }); }
    catch (e) { console.warn('cam capture failed (optional):', e); return; }
    this.camTrack = cam.getVideoTracks()[0];
    this.handlers.onLocalVideo?.(this.camTrack);   // Self-View sofort
    try { await this.link?.publish(this.camTrack, { source: Track.Source.Camera, name: `cam-${this.guestId}` }); }
    catch (e) { console.warn('cam publish failed:', e); }
  }

  private attachRemote(track: RemoteTrack, p: RemoteParticipant): void {
    const host = isHostIdentity(p.identity);
    if (track.kind === Track.Kind.Video) {
      if (host) {
        this.hostVideoReceiver = track.receiver ?? null;
        this.handlers.onHostVideo?.(track.mediaStreamTrack);
        this.applyVideoSync();
      } else {
        const key = tileKey(p, track);
        this.liveCamKeys.add(key);
        this.handlers.onPeerVideo?.(key, track.mediaStreamTrack);
        this.emitPeers();
      }
      return;
    }
    // Audio (Host-Mix + Gäste): <audio> + setSinkId (SP2-Pfad, unverändert).
    this.audioEls.set(`${p.identity}:${track.sid}`, playRemoteAudio(track.mediaStreamTrack, this.opts.outputDeviceId));
    if (host) { this.hostAudioReceiver = track.receiver ?? null; void this.reZero(); }
  }

  private detachRemote(track: RemoteTrack, p: RemoteParticipant): void {
    const host = isHostIdentity(p.identity);
    if (track.kind === Track.Kind.Video) {
      if (host) { this.hostVideoReceiver = null; return; }
      const key = tileKey(p, track);
      this.liveCamKeys.delete(key);
      this.handlers.onPeerVideo?.(key, null);
      this.emitPeers();
      return;
    }
    const k = `${p.identity}:${track.sid}`;
    const el = this.audioEls.get(k);
    if (el) { el.srcObject = null; this.audioEls.delete(k); }
    if (host) this.hostAudioReceiver = null;
  }

  /** Namensband: Mitgäste aus dem Raum; Sprecher nur mit sichtbarer Kamera (wie vor dem Umbau). */
  private emitPeers(): void {
    this.handlers.onPeers?.(this.peers.guests, this.peers.speakers.filter((s) => this.liveCamKeys.has(`sp:${s.speakerId}`)));
  }

  /** Erstausrichtung: festen Gast-Default setzen und Restversatz messen, sobald beide Host-Tracks da sind. */
  private applyVideoSync(): void {
    if (!this.hostVideoReceiver) return;
    setVideoDelay(this.hostVideoReceiver, DEFAULT_OFFSET_MS.guest);   // sofortiger Startwert
    this.videoTargetMs = DEFAULT_OFFSET_MS.guest;
    void this.reZero();
  }

  private async reZero(): Promise<void> {
    if (!this.hostVideoReceiver || !this.hostAudioReceiver) return;
    this.videoTargetMs = await reZeroGuestVideo(this.hostAudioReceiver, this.hostVideoReceiver, this.videoTargetMs);
  }

  private clearRemote(): void {
    for (const el of this.audioEls.values()) el.srcObject = null;
    this.audioEls.clear();
    for (const key of this.liveCamKeys) this.handlers.onPeerVideo?.(key, null);
    this.liveCamKeys.clear();
    this.hostVideoReceiver = null; this.hostAudioReceiver = null;
  }

  /** Zurück im Warteraum (Grace abgelaufen, frischer Raum nach Neustart): keine Medien ohne Zulassung. */
  private dropMedia(): void {
    if (!this.link) return;
    this.link.stop();
    this.link = null;
    this.clearRemote();
  }

  setMuted(muted: boolean): void { if (this.mic) this.mic.enabled = !muted; }   // lokal, wie bisher (1:1)

  leave(): void { this.sig?.send({ type: 'BYE' }); this.teardown(); }

  /** Tab-Schliessen/Navigieren: zuverlässiges Sofort-Leave — ein WS-Frame im Unload wird oft nicht
   *  mehr geflusht, das Beacon schon. Der Raum entfernt den Gast sofort (auch aus LiveKit). */
  byeBeacon(): void {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const url = `/api/conference/${encodeURIComponent(this.token)}/bye`;
    const body = new Blob([JSON.stringify({ guestId: this.guestId })], { type: 'application/json' });
    try { navigator.sendBeacon(url, body); } catch { /* Unload — best effort */ }
  }

  teardown(): void {
    try { this.sig?.close(); } catch { /* ignore */ }
    this.link?.stop();
    this.clearRemote();
    try { this.mic?.stop(); } catch { /* ignore */ }
    this.camTrack?.stop(); this.camTrack = null;
    this.handlers.onLocalVideo?.(null);   // Self-View entfernen
    this.sig = null; this.link = null;
  }
}

// ── Sprecher (video-only; Audio läuft GETRENNT über die L16-SignalingRoom) ──────────────────
export type SpeakerConferenceHandlers = {
  onHostVideo?: (track: MediaStreamTrack) => void;
  onPeerVideo?: (key: string, track: MediaStreamTrack | null) => void;
  onLocalVideo?: (track: MediaStreamTrack | null) => void;   // eigenes Kamerabild (Self-View)
  onPeers?: (guests: ConfPeer[], speakers: ConfSpeakerRef[]) => void;
};

export class SpeakerConference {
  private sig: ConferenceSignaling | null = null;
  private link: MediaLink | null = null;
  private camTrack: MediaStreamTrack | null = null;
  private hostVideoReceiver: RTCRtpReceiver | null = null;
  private liveCamKeys = new Set<string>();
  private peers: { guests: ConfPeer[]; speakers: ConfSpeakerRef[] } = { guests: [], speakers: [] };

  constructor(
    private slug: string, private speakerId: string, private name: string,
    private opts: { cameraDeviceId?: string },
    private handlers: SpeakerConferenceHandlers,
  ) {}

  async join(): Promise<void> {
    if (this.opts.cameraDeviceId) {
      try {
        const cam = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: this.opts.cameraDeviceId } } });
        this.camTrack = cam.getVideoTracks()[0];
        this.handlers.onLocalVideo?.(this.camTrack);   // Self-View
      } catch (e) { console.warn('speaker cam capture failed (optional):', e); }
    }
    const link = new MediaLink(
      () => fetchLivekitToken(`/api/conference/studio/${encodeURIComponent(this.slug)}/token`, { speakerId: this.speakerId }),
      false,   // Sprecher abonniert NUR Video — kein Konferenz-Audio (sein Ton läuft über L16)
      {
        onConnected: async () => {
          if (this.camTrack) await link.publish(this.camTrack, { source: Track.Source.Camera, name: `cam-${this.speakerId}` });
        },
        onPublished: (pub) => { if (pub.kind === Track.Kind.Video) pub.setSubscribed(true); },
        onSubscribed: (track, _pub, p) => this.attachRemote(track, p),
        onUnsubscribed: (track, _pub, p) => this.detachRemote(track, p),
      },
    );
    this.link = link;
    this.sig = new ConferenceSignaling(
      wsUrl(`/api/conference/studio/${encodeURIComponent(this.slug)}?role=speaker`),
      { type: 'SPEAKER_HELLO', speakerId: this.speakerId, name: this.name },
      {
        // Token gibt es erst, wenn der Raum das SPEAKER_HELLO kennt → LiveKit nach dem ersten WS-Open.
        onOpen: () => link.start(),
        onMessage: (m) => this.onMessage(m),
      },
    );
    this.sig.open();
  }

  private onMessage(m: ConferenceMessage): void {
    if (m.type !== 'PEERS') return;
    this.peers = { guests: m.guests, speakers: m.speakers };
    this.emitPeers();
  }

  private attachRemote(track: RemoteTrack, p: RemoteParticipant): void {
    if (track.kind !== Track.Kind.Video) return;   // Sprecher spielt KEIN Konferenz-Audio (L16)
    if (isHostIdentity(p.identity)) {
      this.hostVideoReceiver = track.receiver ?? null;
      if (this.hostVideoReceiver) setVideoDelay(this.hostVideoReceiver, DEFAULT_OFFSET_MS.speaker);   // fester, justierbarer Offset
      this.handlers.onHostVideo?.(track.mediaStreamTrack);
      return;
    }
    const key = tileKey(p, track);
    this.liveCamKeys.add(key);
    this.handlers.onPeerVideo?.(key, track.mediaStreamTrack);
    this.emitPeers();
  }

  private detachRemote(track: RemoteTrack, p: RemoteParticipant): void {
    if (track.kind !== Track.Kind.Video) return;
    if (isHostIdentity(p.identity)) { this.hostVideoReceiver = null; return; }
    const key = tileKey(p, track);
    this.liveCamKeys.delete(key);
    this.handlers.onPeerVideo?.(key, null);
    this.emitPeers();
  }

  private emitPeers(): void {
    this.handlers.onPeers?.(this.peers.guests, this.peers.speakers.filter((s) => this.liveCamKeys.has(`sp:${s.speakerId}`)));
  }

  /** Offset live nachjustieren (versteckter Regler im Sprecher-/Cockpit-UI; Default 54 ms). */
  setOffsetMs(ms: number): void { if (this.hostVideoReceiver) setVideoDelay(this.hostVideoReceiver, ms); }

  /** Bewusstes Leave: Sprecher SOFORT aus dem Roster (und aus LiveKit) nehmen. Beacon übersteht den
   *  Tab-Unload zuverlässiger als ein WS-Frame. */
  byeBeacon(): void {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const url = `/api/conference/studio/${encodeURIComponent(this.slug)}/bye`;
    const body = new Blob([JSON.stringify({ speakerId: this.speakerId })], { type: 'application/json' });
    try { navigator.sendBeacon(url, body); } catch { /* Unload — best effort */ }
  }

  teardown(): void {
    this.byeBeacon();   // echtes Leave (stop/giveUp/unmount) → Kachel sofort weg, kein Grace-Freeze
    try { this.sig?.close(); } catch { /* ignore */ }
    this.link?.stop();
    this.camTrack?.stop(); this.camTrack = null;
    this.handlers.onLocalVideo?.(null);   // Self-View entfernen
    for (const key of this.liveCamKeys) this.handlers.onPeerVideo?.(key, null);
    this.hostVideoReceiver = null;
    this.sig = null; this.link = null; this.liveCamKeys.clear();
  }
}

/** Stabile guestId pro Token (überlebt Reconnect/Reload im selben Tab → Raum matcht den Wartenden). */
export function getOrCreateGuestId(token: string): string {
  const key = `tro-guestId:${token}`;
  let id = sessionStorage.getItem(key);
  if (!id) { id = crypto.randomUUID(); sessionStorage.setItem(key, id); }
  return id;
}
